import React from 'react';
import classNames from 'classnames/bind';
import { Button, defaultTheme, InlineNotification, Loader, Typography } from 'gd-design-library';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import Image from 'next/image';
import styles from './Messages.module.scss';
import { ChatMessage, ChatMessageType } from '../../../../utils/SmartSearchContext';
import { SuggestedAnswer } from '../../../../services/search/interfaces';
import retryIcon from '@assets/images/retry.svg';

const cx = classNames.bind(styles);
const CN = 'messages';
const messagesClassName = CN;
const chatMessageClassName = `${CN}__chat-message`;
const chatMessageUserClassName = `${CN}__chat-message--user`;
const chatMessageAiClassName = `${CN}__chat-message--ai`;
const messageBubbleClassName = `${CN}__message-bubble`;
const chatImageClassName = `${CN}__chat-image`;
const chatTextClassName = `${CN}__chat-text`;
const messageBubbleUserClassName = `${CN}__message-bubble--user`;
const messageBubbleAiClassName = `${CN}__message-bubble--ai`;
const loadingIndicatorClassName = `${CN}__loading-indicator`;
const errorNotification = `${CN}__error-notification`;
const errorNotificationContent = `${CN}__error-notification-content`;
const suggestedAnswersClassName = `${CN}__suggested-answers`;
const suggestedAnswersContainerClassName = `${CN}__suggested-answers-container`;
const suggestedAnswerButtonClassName = `${CN}__suggested-answer-button`;

interface MessagesProps {
  chatHistory: ChatMessage[];
  isLoading: boolean;
  lastFailedRequest: {
    type: 'message' | 'answer';
    params: {
      userMessage?: string;
      answer?: SuggestedAnswer;
    };
  } | null;
  currentSuggestedAnswers: SuggestedAnswer[];
  chatScrollRef: React.RefObject<HTMLDivElement>;
  onSuggestedAnswerClick: (answer: SuggestedAnswer) => Promise<void>;
  onRetryRequest: () => Promise<void>;
  scrollToBottom: () => void;
}

export const Messages: React.FC<MessagesProps> = ({
  chatHistory,
  isLoading,
  lastFailedRequest,
  currentSuggestedAnswers,
  chatScrollRef,
  onSuggestedAnswerClick,
  onRetryRequest,
  scrollToBottom
}) => (
  <div className={ cx(messagesClassName) } ref={ chatScrollRef }>
    {chatHistory?.map(message => {
      const isUserMessage = message.type === ChatMessageType.USER;

      return (
        <div
          key={ message.id }
          data-message-id={ message.id }
          className={ cx(chatMessageClassName, isUserMessage ? chatMessageUserClassName : chatMessageAiClassName) }
        >
          <div
            className={ cx(
              messageBubbleClassName,
              isUserMessage ? messageBubbleUserClassName : messageBubbleAiClassName
            ) }
          >
            {message.image && (
              <img
                src={ URL.createObjectURL(message.image) }
                alt='Uploaded image'
                className={ cx(chatImageClassName) }
                onLoad={ scrollToBottom }
                onError={ (e): void => {
                  (e.target as HTMLImageElement).style.display = 'none';
                } }
              />
            )}
            {message.content && (
              <Typography variant='small' className={ cx(chatTextClassName) }>
                <ReactMarkdown
                  remarkPlugins={ [ remarkGfm ] }
                  components={ { a: props => <a { ...props } target='_blank' rel='noopener noreferrer' /> } }
                >
                  {message.content}
                </ReactMarkdown>
              </Typography>
            )}
          </div>
        </div>
      );
    })}
    {isLoading && (
      <Loader
        name='dots'
        rounded='round'
        size='xs'
        variant='inline'
        withWrapper={ false }
        className={ cx(loadingIndicatorClassName) }
      />
    )}
    {lastFailedRequest && (
      <div className={ cx(errorNotification) }>
        <InlineNotification variant='error'>
          <div className={ cx(errorNotificationContent) }>
            <Typography variant='small' color={ defaultTheme.colors.red[60] }>
              Couldn&apos;t send message.
            </Typography>
            <Button
              variant='outlined'
              onClick={ onRetryRequest }
              disabled={ isLoading }
              iconEnd={
                <Image
                  src={ retryIcon.src }
                  alt='Retry'
                  width={ 20 }
                  height={ 20 }
                  style={ isLoading ? { opacity: 0.4 } : undefined }
                />
              }
            >
              Try again
            </Button>
          </div>
        </InlineNotification>
      </div>
    )}
    {currentSuggestedAnswers.length > 0 && (
      <div className={ cx(suggestedAnswersClassName) }>
        <div className={ cx(suggestedAnswersContainerClassName) }>
          {currentSuggestedAnswers.map((answer, index) => (
            <Button
              key={ index }
              variant='outlined'
              onClick={ (): Promise<void> => onSuggestedAnswerClick(answer) }
              className={ cx(suggestedAnswerButtonClassName) }
            >
              {answer.displayValue || answer.value}
            </Button>
          ))}
        </div>
      </div>
    )}
  </div>
);

